import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import QRCode from 'qrcode';

import { AuthApiService } from '../../_core/_services/auth-api.service';
import { NotificationService } from '../../_core/_services/notification.service';

type TotpSetupData = { otpauthUri: string; manualEntryKey: string };

@Component({
  selector: 'app-tfa',
  standalone: false,
  templateUrl: './tfa.html',
  styleUrl: './tfa.css',
})
export class AccountTfa implements OnInit {
  // template expects these names
  loadingSetup = false;
  loadingConfirm = false;

  error: string | null = null;
  success = false;

  submitted = false; // template uses "submitted"

  setupData: TotpSetupData | null = null;

  qrDataUrl = '';
  otpauthUri = '';

  confirmForm: FormGroup;

  constructor(
    private fb: FormBuilder,
    private authApi: AuthApiService,
    private notify: NotificationService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.confirmForm = this.fb.group({
      code: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
    });
  }

  ngOnInit(): void {
    this.setup();
  }

  setup() {
    this.error = null;
    this.success = false;
    this.loadingSetup = true;

    this.authApi.totpSetup().subscribe({
      next: async (res) => {
        this.loadingSetup = false;

        if (!res?.success) {
          this.error = res?.message ?? 'Failed to start 2FA setup.';
          return;
        }

        this.setupData = res.data;
        this.otpauthUri = res.data.otpauthUri;

        // Generate QR from otpauthUri
        try {
          this.qrDataUrl = await QRCode.toDataURL(this.otpauthUri);
        } catch {
          this.qrDataUrl = '';
          this.notify.showWarning('Could not render QR code. Use manual key instead.', "QR Code Error");
        }
      },
      error: (err) => {
        this.loadingSetup = false;
        this.error = err?.error?.message ?? 'Failed to start 2FA setup.';
        this.notify.showError(this.error!, "2FA Setup");
      },
    });
  }

  async copyKey(key?: string | null) {
    const value = (key ?? '').trim();
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      this.notify.showSuccess('Key copied.', "2FA Key");
    } catch {
      // fallback
      const ta = document.createElement('textarea');
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      this.notify.showSuccess('Key copied.', "2FA Key");
    }
  }

  confirmTotp() {
    this.submitted = true;
    this.error = null;

    if (this.confirmForm.invalid) return;

    this.loadingConfirm = true;

    const code = (this.confirmForm.get('code')?.value ?? '').toString().trim();

    this.authApi.totpConfirm({ code }).subscribe({
      next: (res) => {
        this.loadingConfirm = false;

        if (!res?.success) {
          this.error = res?.message ?? 'Invalid code. Try again.';
          return;
        }

        this.success = true;
        this.notify.showSuccess('2FA enabled successfully.', "2FA Setup");
      },
      error: (err) => {
        this.loadingConfirm = false;
        this.error = err?.error?.message ?? 'Confirmation failed. Try again.';
        this.notify.showError(this.error!, "2FA Setup");
      },
    });
  }

  back() {
    this.router.navigate(['/account'], {
      queryParams: { tab: 'accounts' },
      queryParamsHandling: 'merge',
    });
  }
}